import * as React from 'react'
import { WebMercatorViewport, DeckGlView } from '@deck.gl/core'

type Feature = {
  type: string
  parentId?: number | undefined
  id?: any
  propperties?: any
  geometry: any
}

export type DeckGlZoomProps = {
  feature: null | undefined | Feature
  minZoom: number
  maxZoom: number
  zoomStep: number
}

type Coordinates = [number, number]

type BBox = [Coordinates, Coordinates]

const CH_BBOX: BBox = [
  [5.8358140744676303, 45.659168946713827],
  [10.979311848153316, 47.869910020393519],
]

interface ViewState {
  width: number
  height: number
  longitude: number
  latitude: number
  [key: string]: any
}

/**
 * Padding (in lat / lon units?) that is added to the bounding box. It basically
 * defines how much beyond the bounding box of the feature the user will see.
 * Also how far out the user can zoom.
 */
const PADDING = 0.02

export const useDeckGLZoom = ({ feature, minZoom, maxZoom, zoomStep }: DeckGlZoomProps) => {
  const constrainZoom = React.useCallback(
    (viewState: any, bbox: BBox, { padding = 0 }: { padding?: number } = {}) => {
      const vp = new WebMercatorViewport(viewState)

      const { width, height, zoom, longitude, latitude } = viewState

      const [x, y] = vp.project([longitude, latitude])
      const [x0, y1] = vp.project(bbox[0])
      const [x1, y0] = vp.project(bbox[1])

      const fitted = vp.fitBounds(bbox, { padding })

      const [cx, cy] = vp.project([fitted.longitude, fitted.latitude])

      const h = height - padding * 2
      const w = width - padding * 2

      const h2 = h / 2
      const w2 = w / 2

      const y2 = y1 - y0 < h ? cy : y - h2 < y0 ? y0 + h2 : y + h2 > y1 ? y1 - h2 : y
      const x2 = x1 - x0 < w ? cx : x - w2 < x0 ? x0 + w2 : x + w2 > x1 ? x1 - w2 : x

      const p = vp.unproject([x2, y2])

      return {
        ...viewState,
        transitionDuration: 0,
        transitionInterpolator: null,
        zoom: Math.min(Math.max(zoom, fitted.zoom), maxZoom),
        maxZoom,
        minZoom,
        longitude: p[0],
        latitude: p[1],
      }
    },
    [minZoom, maxZoom],
  )

  const [[minLon, minLat], [maxLon, maxLat]] = useBoundingBox(feature)
  const boundingBox: BBox = React.useMemo(() => {
    return [
      [minLon - PADDING, minLat - PADDING],
      [maxLon + PADDING, maxLat + PADDING],
    ]
  }, [minLon, minLat, maxLon, maxLat])

  const zoomIn = (viewState: DeckGlView) =>
    constrainZoom({ ...viewState, zoom: (viewState.zoom || minZoom) + zoomStep }, boundingBox)

  const zoomOut = (viewState: DeckGlView) =>
    constrainZoom({ ...viewState, zoom: (viewState.zoom || maxZoom) - zoomStep }, boundingBox)

  const restrictZoom = React.useCallback(
    (viewState: DeckGlView) => constrainZoom(viewState, boundingBox),
    [constrainZoom, boundingBox],
  )
  return { zoomOut, zoomIn, restrictZoom, boundingBox }
}

export const useFitBoundingBox = (feature: null | undefined | Feature) => {
  const boundingBox = useBoundingBox(feature)
  return React.useCallback(
    (viewState: ViewState, zoomPadding: number) => {
      const viewPort = new WebMercatorViewport(viewState)
      const { zoom } = viewPort.fitBounds(boundingBox)
      const [[minLon, minLat], [maxLon, maxLat]] = boundingBox
      const centerLon = (minLon + maxLon) / 2
      const centerLat = (minLat + maxLat) / 2
      return { ...viewState, zoom: zoom + zoomPadding, latitude: centerLat, longitude: centerLon }
    },
    [boundingBox],
  )
}

/**
 * Used to calculate contiguous cantons
 */
const calculatePolygonBoundingBox = (feature: Coordinates[][]) => {
  const polygon = feature[0]
  const [lon, lat] = polygon[0]
  let maxLon = lon
  let minLon = lon
  let maxLat = lat
  let minLat = lat
  polygon.forEach(([lon, lat]: Coordinates) => {
    maxLon = Math.max(maxLon, lon)
    minLon = Math.min(minLon, lon)
    maxLat = Math.max(maxLat, lat)
    minLat = Math.min(minLat, lat)
  })
  return [maxLon, minLon, maxLat, minLat]
}

/**
 * Used to calculate non contiguous cantons
 */
const calculateMultiPolygonBounbingBox = (feature: Coordinates[][][]) => {
  const [lon, lat] = feature[0][0][0]
  let maxLon = lon
  let minLon = lon
  let maxLat = lat
  let minLat = lat
  feature.forEach((polygon: Coordinates[][]) => {
    polygon[0].forEach(([lon, lat]: Coordinates) => {
      maxLon = Math.max(maxLon, lon)
      minLon = Math.min(minLon, lon)
      maxLat = Math.max(maxLat, lat)
      minLat = Math.min(minLat, lat)
    })
  })
  return [maxLon, minLon, maxLat, minLat]
}

function useBoundingBox(feature: null | undefined | Feature): BBox {
  return React.useMemo(() => {
    if (!feature) {
      return CH_BBOX
    }

    const { coordinates, type } = feature.geometry
    const [maxLon, minLon, maxLat, minLat] =
      type === 'MultiPolygon' ? calculateMultiPolygonBounbingBox(coordinates) : calculatePolygonBoundingBox(coordinates)

    return [
      [minLon, minLat],
      [maxLon, maxLat],
    ]
  }, [feature])
}
